import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import MoveToInboxIcon from '@mui/icons-material/MoveToInboxOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import { apiClient } from '@/api/axiosClient';
import StatCard from '@/components/common/StatCard';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import StatusChip from '@/components/common/StatusChip';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import ChartCard from '@/components/dashboard/ChartCard';
import DashboardBarChart from '@/components/dashboard/DashboardBarChart';
import DashboardLineChart from '@/components/dashboard/DashboardLineChart';
import DashboardPieChart from '@/components/dashboard/DashboardPieChart';
import { useCategoryColors } from '@/pages/dashboard/dashboardColors';
import { average, groupCountBy, groupSumByMonth } from '@/lib/dashboardAggregations';
import { useDateFormat } from '@/hooks/useDateFormat';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import { withId } from '@/pages/reports/reportHelpers';
import type { GrnRegisterRow, PutawayTatRow, QcAnalysisRow } from '@/types/reports';

const STATUS_SLICE_COLORS = ['#4edea3', '#ba1a1a', '#ffb951', '#0566d9', '#6b6f76'];

export default function InboundQualityPanel() {
  const { t } = useTranslation('dashboard');
  const { t: tr } = useTranslation('reports');
  const { formatDate } = useDateFormat();
  const colors = useCategoryColors();
  const { data: processConfiguration } = useProcessConfiguration();
  // GRN itself is never disabled (see GoodsReceiptNote's doc comment), so accepted/rejected qty
  // always apply — QC/Putaway are individually toggleable, so their own sub-sections hide when off
  // rather than showing a metric with nothing behind it.
  const qcEnabled = processConfiguration ? processConfiguration.qcEnabled : true;
  const putawayEnabled = processConfiguration ? processConfiguration.putawayEnabled : true;

  const { data: grns = [], isLoading: loadingGrn } = useQuery({
    queryKey: ['reports', 'grn-register'],
    queryFn: async () => (await apiClient.get<GrnRegisterRow[]>('/reports/grn-register')).data,
  });
  const { data: qcAnalysis = [], isLoading: loadingQc } = useQuery({
    queryKey: ['reports', 'qc-analysis'],
    queryFn: async () => (await apiClient.get<QcAnalysisRow[]>('/reports/qc-analysis')).data,
  });
  const { data: putawayTat = [] } = useQuery({
    queryKey: ['reports', 'putaway-tat'],
    queryFn: async () => (await apiClient.get<PutawayTatRow[]>('/reports/putaway-tat')).data,
  });

  const totalGrns = useMemo(() => new Set(grns.map((r) => r.grnId)).size, [grns]);
  const acceptedQty = useMemo(() => grns.reduce((sum, r) => sum + r.acceptedQty, 0), [grns]);
  const rejectedQty = useMemo(() => grns.reduce((sum, r) => sum + r.rejectedQty, 0), [grns]);
  const avgTat = useMemo(() => average(putawayTat.map((r) => r.tatHours).filter((v): v is number => v != null)), [putawayTat]);

  const monthlyAcceptReject = useMemo(
    () => groupSumByMonth(grns, (r) => r.grnDate, [
      { key: 'accepted', value: (r) => r.acceptedQty },
      { key: 'rejected', value: (r) => r.rejectedQty },
    ]),
    [grns],
  );
  const monthlyGrnCount = useMemo(() => groupSumByMonth(grns, (r) => r.grnDate, [{ key: 'count', value: () => 1 }]), [grns]);
  const qcStatusDistribution = useMemo(() => groupCountBy(qcAnalysis, (r) => r.status), [qcAnalysis]);

  const grnColumns: DataTableColumn<ReturnType<typeof withId<GrnRegisterRow>>[number]>[] = [
    { field: 'grnNumber', headerName: tr('columns.number') },
    { field: 'supplierName', headerName: tr('columns.supplier'), filterable: true },
    { field: 'grnDate', headerName: tr('columns.date'), render: (r) => formatDate(r.grnDate) },
    { field: 'status', headerName: tr('columns.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    { field: 'acceptedQty', headerName: tr('columns.acceptedQty'), align: 'right' },
    { field: 'rejectedQty', headerName: tr('columns.rejectedQty'), align: 'right' },
  ];

  return (
    <Stack spacing={3}>
      <GridContainer columns={12}>
        <GridItem xs={12} sm={6} md={3}>
          <StatCard label={t('inboundQuality.totalGrns')} value={totalGrns} icon={<MoveToInboxIcon />} accentColor={colors.inboundQuality} />
        </GridItem>
        <GridItem xs={12} sm={6} md={3}>
          <StatCard label={t('inboundQuality.acceptedQty')} value={acceptedQty.toLocaleString()} icon={<CheckCircleOutlineIcon />} accentColor={colors.outbound} />
        </GridItem>
        <GridItem xs={12} sm={6} md={3}>
          <StatCard label={t('inboundQuality.rejectedQty')} value={rejectedQty.toLocaleString()} icon={<CancelOutlinedIcon />} accentColor={colors.adjustments} />
        </GridItem>
        {putawayEnabled && (
          <GridItem xs={12} sm={6} md={3}>
            <StatCard label={t('inboundQuality.avgPutawayTat')} value={avgTat > 0 ? `${avgTat.toFixed(1)} hrs` : '—'} icon={<TimerOutlinedIcon />} accentColor={colors.inboundQuality} />
          </GridItem>
        )}
      </GridContainer>

      <GridContainer columns={12} spacing={2}>
        <GridItem xs={12} md={qcEnabled ? 7 : 12}>
          <ChartCard title={t('inboundQuality.acceptRejectTitle')} subtitle={t('inboundQuality.acceptRejectSubtitle')} loading={loadingGrn} empty={!loadingGrn && monthlyAcceptReject.length === 0}>
            <DashboardBarChart
              categories={monthlyAcceptReject.map((m) => m.month)}
              series={[
                { label: tr('columns.acceptedQty'), data: monthlyAcceptReject.map((m) => m.values.accepted), color: colors.outbound },
                { label: tr('columns.rejectedQty'), data: monthlyAcceptReject.map((m) => m.values.rejected), color: colors.adjustments },
              ]}
            />
          </ChartCard>
        </GridItem>
        {qcEnabled && (
          <GridItem xs={12} md={5}>
            <ChartCard title={t('inboundQuality.qcStatusTitle')} loading={loadingQc} empty={!loadingQc && qcStatusDistribution.length === 0}>
              <DashboardPieChart data={qcStatusDistribution.map((s, i) => ({ label: s.key, value: s.count, color: STATUS_SLICE_COLORS[i % STATUS_SLICE_COLORS.length] }))} />
            </ChartCard>
          </GridItem>
        )}
      </GridContainer>

      <ChartCard title={t('inboundQuality.grnTrendTitle')} loading={loadingGrn} empty={!loadingGrn && monthlyGrnCount.length === 0} height={260}>
        <DashboardLineChart
          categories={monthlyGrnCount.map((m) => m.month)}
          series={[{ label: t('inboundQuality.grnCount'), data: monthlyGrnCount.map((m) => m.values.count), color: colors.inboundQuality, area: true }]}
          height={260}
        />
      </ChartCard>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
          {t('inboundQuality.grnTableTitle')}
        </Typography>
        <DataTable columns={grnColumns} rows={withId(grns, (r) => `${r.grnId}-${r.materialCode}`)} loading={loadingGrn} exportable={false} defaultRowsPerPage={5} rowsPerPageOptions={[5, 10, 25]} />
      </Paper>
    </Stack>
  );
}
